(() => {
  const namespace = window.AuremeStaffstartDefaultOverrides;
  const runtime = namespace?.runtime;
  if (!runtime) return;

  const { findAncestorUntilRoot, getTrimmedText } = runtime;

  const TYPE_HEADING_TEXT = 'タイプから探す';
  const TYPE_HEADING_DISPLAY_TEXT = '性別';
  const HEIGHT_HEADING_TEXT = '身長から探す';
  const HEIGHT_HEADING_DISPLAY_TEXT = '身長';
  const STORE_HEADING_TEXT = '店舗から探す';
  const STORE_HEADING_DISPLAY_TEXT = '店舗';
  const BRAND_HEADING_TEXT = 'ブランドから探す';
  const FILTER_TOGGLE_TEXT = '絞り込む';
  const FILTER_ALL_OPTION_TEXT = 'すべて';
  const DEFAULT_SELECT_OPTION_TEXT = '選択してください';
  const HASHTAG_HEADING_TEXT = 'おすすめのハッシュタグ';
  const HASHTAG_HEADING_DISPLAY_TEXT = '人気のハッシュタグ';
  const HASHTAG_SEARCH_PLACEHOLDER_TEXT = 'キーワードで検索';
  const HASHTAG_SEARCH_PLACEHOLDER_DISPLAY_TEXT = 'ハッシュタグで検索';
  const FILTER_CONDITION_TEXT = '検索条件';
  const FILTER_CONDITION_DISPLAY_TEXT = '現在の絞り込み条件';

  const uiTextReplacements = new Map([
    [TYPE_HEADING_TEXT, TYPE_HEADING_DISPLAY_TEXT],
    [HEIGHT_HEADING_TEXT, HEIGHT_HEADING_DISPLAY_TEXT],
    [STORE_HEADING_TEXT, STORE_HEADING_DISPLAY_TEXT],
    [HASHTAG_HEADING_TEXT, HASHTAG_HEADING_DISPLAY_TEXT],
    [FILTER_CONDITION_TEXT, FILTER_CONDITION_DISPLAY_TEXT],
    [`${FILTER_CONDITION_TEXT}:`, `${FILTER_CONDITION_DISPLAY_TEXT}:`],
    [`${FILTER_CONDITION_TEXT}：`, `${FILTER_CONDITION_DISPLAY_TEXT}：`],
  ]);
  const typeFilterTextReplacements = new Map([
    ['MENS', 'MEN'],
    ['WOMENS', 'WOMEN'],
  ]);

  const textMatchers = {
    type: new Set([TYPE_HEADING_TEXT, TYPE_HEADING_DISPLAY_TEXT]),
    height: new Set([HEIGHT_HEADING_TEXT, HEIGHT_HEADING_DISPLAY_TEXT]),
    store: new Set([STORE_HEADING_TEXT, STORE_HEADING_DISPLAY_TEXT]),
    brand: new Set([BRAND_HEADING_TEXT]),
    hashtag: new Set([HASHTAG_HEADING_TEXT]),
    hashtagTypography: new Set([HASHTAG_HEADING_TEXT, HASHTAG_HEADING_DISPLAY_TEXT]),
    hashtagSearchPlaceholder: new Set([HASHTAG_SEARCH_PLACEHOLDER_TEXT]),
    hashtagSearchTypographyPlaceholder: new Set([
      HASHTAG_SEARCH_PLACEHOLDER_TEXT,
      HASHTAG_SEARCH_PLACEHOLDER_DISPLAY_TEXT,
    ]),
    filterCondition: new Set([
      FILTER_CONDITION_TEXT,
      FILTER_CONDITION_DISPLAY_TEXT,
      `${FILTER_CONDITION_TEXT}:`,
      `${FILTER_CONDITION_DISPLAY_TEXT}:`,
      `${FILTER_CONDITION_TEXT}：`,
      `${FILTER_CONDITION_DISPLAY_TEXT}：`,
    ]),
    typeFilter: new Set(typeFilterTextReplacements.keys()),
  };

  const attributes = {
    filterToggle: 'data-aureme-staffstart-filter-toggle',
    typeFilterItem: 'data-aureme-staffstart-type-filter-hidden',
    filterAllOption: 'data-aureme-staffstart-filter-all-option',
    brandFilter: 'data-aureme-staffstart-brand-filter',
    filterHeading: 'data-aureme-staffstart-coordinate-index-filter-heading',
    hashtagHeading: 'data-aureme-staffstart-coordinate-index-hashtag-heading',
    hashtagSearch: 'data-aureme-staffstart-coordinate-index-hashtag-search',
    staffName: 'data-aureme-staffstart-coordinate-index-staff-name',
  };

  const datasetKeys = {
    filterAllOptionOriginalText: 'auremeStaffstartFilterAllOptionOriginalText',
    filterAllOptionOriginalDisplay: 'auremeStaffstartFilterAllOptionOriginalDisplay',
    filterAllOptionOriginalDisabled: 'auremeStaffstartFilterAllOptionOriginalDisabled',
    filterAllOptionOriginalHidden: 'auremeStaffstartFilterAllOptionOriginalHidden',
  };

  const selectors = {
    headingCandidate: 'p, span, label, div',
    brandFilterControl: 'select, a[href*="label="]',
    typeFilterControl: 'a[href*="genre="], select',
    heightFilterControl: 'select, a[href*="height="]',
    storeFilterControl: 'a[href*="shopId="], select',
    staffDetailLink: 'a[href*="/apps/staffstart/staffs/"]',
    coordinateDetailLink: 'a[href*="/apps/staffstart/coordinates/"]',
    staffNameText: 'p, span, div, strong, b, small',
  };
  selectors.filterTogglePanelControl = [
    selectors.brandFilterControl,
    selectors.typeFilterControl,
    selectors.storeFilterControl,
  ].join(', ');

  const constants = {
    hiddenTypeFilterValues: new Set(['KIDS']),
    filterAllOptionText: FILTER_ALL_OPTION_TEXT,
    defaultSelectOptionText: DEFAULT_SELECT_OPTION_TEXT,
    filterToggleText: FILTER_TOGGLE_TEXT,
    filterParentSearchLimit: 8,
    coordinateCardParentSearchLimit: 6,
    staffHeightPattern: /^\d+(?:\.\d+)?cm$/,
  };

  /**
   * STAFF SNAP 一覧の UI 文言を完全一致で同期する。
   * @param {HTMLElement} element - 同期対象要素
   * @param {Map<string, string>} replacements - 置換対象文言
   */
  function syncElementText(element, replacements) {
    const replacementText = replacements.get(getTrimmedText(element));
    if (!replacementText) return;

    if (element.textContent !== replacementText) {
      element.textContent = replacementText;
    }
  }

  /**
   * STAFF SNAP 一覧のハッシュタグ検索 placeholder を同期する。
   * @param {HTMLElement} element - 同期対象要素
   */
  function syncHashtagSearchPlaceholder(element) {
    const currentPlaceholder = element.getAttribute('placeholder');
    if (currentPlaceholder !== HASHTAG_SEARCH_PLACEHOLDER_TEXT) return;

    element.setAttribute('placeholder', HASHTAG_SEARCH_PLACEHOLDER_DISPLAY_TEXT);
  }

  /**
   * 見出しから、指定した絞り込み UI を含むブロックを探す。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @param {HTMLElement} heading - 絞り込み見出し
   * @param {string} controlSelector - 絞り込み UI の selector
   * @returns {HTMLElement | null}
   */
  function findFilterBlock(root, heading, controlSelector) {
    return findAncestorUntilRoot(
      heading.parentElement,
      root,
      (element) => Boolean(element.querySelector(controlSelector)),
      constants.filterParentSearchLimit,
    );
  }

  /**
   * 見出し文言の候補と selector から絞り込みブロックを一覧する。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @param {Set<string>} textSet - 探索する見出し文言
   * @param {string} controlSelector - 絞り込み UI の selector
   * @returns {Set<HTMLElement>}
   */
  function collectFilterBlocks(root, textSet, controlSelector) {
    const blocks = new Set();

    for (const heading of root.querySelectorAll(selectors.headingCandidate)) {
      if (!(heading instanceof HTMLElement)) continue;
      if (!textSet.has(getTrimmedText(heading))) continue;

      const block = findFilterBlock(root, heading, controlSelector);
      if (block) blocks.add(block);
    }

    return blocks;
  }

  /**
   * 指定した絞り込み見出し要素を一覧する。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @param {Set<string>} textSet - 探索する見出し文言
   * @param {string} controlSelector - 絞り込み UI の selector
   * @returns {Set<HTMLElement>}
   */
  function collectFilterHeadings(root, textSet, controlSelector) {
    const headings = new Set();

    for (const heading of root.querySelectorAll(selectors.headingCandidate)) {
      if (!(heading instanceof HTMLElement)) continue;
      if (heading.children.length > 0) continue;
      if (!textSet.has(getTrimmedText(heading))) continue;
      if (!findFilterBlock(root, heading, controlSelector)) continue;

      headings.add(heading);
    }

    return headings;
  }

  /**
   * 指定文言と完全一致する要素を一覧する。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @param {string} selector - 探索対象 selector
   * @param {Set<string>} textSet - 探索する文言
   * @returns {Set<HTMLElement>}
   */
  function collectElementsByExactText(root, selector, textSet) {
    const elements = new Set();

    for (const element of root.querySelectorAll(selector)) {
      if (!(element instanceof HTMLElement)) continue;
      if (element.children.length > 0) continue;
      if (!textSet.has(getTrimmedText(element))) continue;

      elements.add(element);
    }

    return elements;
  }

  /**
   * 指定属性値と完全一致する要素を一覧する。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @param {string} selector - 探索対象 selector
   * @param {string} attributeName - 探索する属性名
   * @param {Set<string>} valueSet - 探索する属性値
   * @returns {Set<HTMLElement>}
   */
  function collectElementsByExactAttribute(root, selector, attributeName, valueSet) {
    const elements = new Set();

    for (const element of root.querySelectorAll(selector)) {
      if (!(element instanceof HTMLElement)) continue;
      if (!valueSet.has(element.getAttribute(attributeName) || '')) continue;

      elements.add(element);
    }

    return elements;
  }

  /**
   * STAFF SNAP 一覧の絞り込み見出しを一覧する。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @returns {Set<HTMLElement>}
   */
  function collectFilterHeadingElements(root) {
    return new Set([
      ...collectFilterHeadings(root, textMatchers.type, selectors.typeFilterControl),
      ...collectFilterHeadings(root, textMatchers.height, selectors.heightFilterControl),
      ...collectFilterHeadings(root, textMatchers.store, selectors.storeFilterControl),
    ]);
  }

  namespace.coordinateIndex = {
    attributes,
    collectElementsByExactAttribute,
    collectElementsByExactText,
    collectFilterBlocks,
    collectFilterHeadingElements,
    constants,
    datasetKeys,
    selectors,
    syncElementText,
    syncHashtagSearchPlaceholder,
    textMatchers,
    typeFilterTextReplacements,
    uiTextReplacements,
  };
})();
